/* Wave 12.18 (Vania 1 Oct) · public legal pages (/privacy + /terms).
   Readable without signing in. Light theme forced. Max column ~680px.
   Tiny inline markdown renderer — supports: # ## ### headings, **bold**,
   *italic*, - bullets, a single pipe-table, [text](url) links, paragraphs.
   Keeps the exact text as written (no reword). */
import React, { useEffect } from "react";
import { Link } from "react-router-dom";

const META = "#4A4138";

function escapeHtml(s) {
  return (s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// Inline formatters: **bold**, *italic*, [t](u). Done after escapeHtml.
function inline(line) {
  let s = escapeHtml(line);
  s = s.replace(/\[([^\]]+)\]\(([^)]+)\)/g,
    (_, t, u) => `<a href="${u}" target="_blank" rel="noreferrer" style="color:#1A1A30;text-decoration:underline">${t}</a>`);
  s = s.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  s = s.replace(/\*([^*]+)\*/g, "<em>$1</em>");
  return s;
}

function renderBlocks(src) {
  const lines = src.split(/\r?\n/);
  const out = [];
  let i = 0;
  let key = 0;
  const nk = () => `b${key++}`;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) { i++; continue; }
    // Headings
    const h = /^(#{1,3})\s+(.*)$/.exec(line);
    if (h) {
      const lvl = h[1].length;
      const txt = h[2].trim();
      const Tag = `h${lvl}`;
      const size = lvl === 1 ? 32 : lvl === 2 ? 22 : 17;
      const mt   = lvl === 1 ? 0 : lvl === 2 ? 32 : 20;
      out.push(React.createElement(Tag, {
        key: nk(),
        style: { fontFamily: "Poppins", fontWeight: 800, letterSpacing: "-0.02em",
                 fontSize: size, color: "#1A1A30", marginTop: mt, marginBottom: 10 },
        dangerouslySetInnerHTML: { __html: inline(txt) },
      }));
      i++;
      continue;
    }
    // Table
    if (line.trim().startsWith("|") && lines[i + 1] && /^\|[-\s|]+\|$/.test(lines[i + 1].trim())) {
      const header = line.trim().slice(1, -1).split("|").map((s) => s.trim());
      i += 2;
      const rows = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) {
        rows.push(lines[i].trim().slice(1, -1).split("|").map((s) => s.trim()));
        i++;
      }
      out.push(
        <table key={nk()}
               style={{ borderCollapse: "collapse", width: "100%", margin: "14px 0",
                        fontFamily: "Arial", fontSize: 14, color: "#1A1A30" }}>
          <thead>
            <tr>{header.map((h, hi) => (
              <th key={hi} style={{ borderBottom: "1px solid #E4D7C0", padding: "8px 10px",
                                     textAlign: "left", fontWeight: 700, background: "#F7EFDF" }}>
                <span dangerouslySetInnerHTML={{ __html: inline(h) }} />
              </th>
            ))}</tr>
          </thead>
          <tbody>
            {rows.map((r, ri) => (
              <tr key={ri}>{r.map((c, ci) => (
                <td key={ci} style={{ borderBottom: "1px solid #F1E7D6", padding: "8px 10px",
                                       verticalAlign: "top" }}>
                  <span dangerouslySetInnerHTML={{ __html: inline(c) }} />
                </td>
              ))}</tr>
            ))}
          </tbody>
        </table>
      );
      continue;
    }
    // Bullet list
    if (/^\s*-\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*-\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*-\s+/, ""));
        i++;
      }
      out.push(
        <ul key={nk()} style={{ margin: "8px 0 10px 22px", padding: 0,
                                 fontFamily: "Arial", fontSize: 15, lineHeight: 1.55, color: "#1A1A30" }}>
          {items.map((it, ii) => (
            <li key={ii} style={{ marginBottom: 4 }}
                dangerouslySetInnerHTML={{ __html: inline(it) }} />
          ))}
        </ul>
      );
      continue;
    }
    // Paragraph: collect consecutive non-empty, non-structural lines.
    const buf = [];
    while (i < lines.length && lines[i].trim() &&
           !/^(#{1,3})\s/.test(lines[i]) &&
           !lines[i].trim().startsWith("|") &&
           !/^\s*-\s+/.test(lines[i])) {
      buf.push(lines[i]);
      i++;
    }
    out.push(
      <p key={nk()} style={{ fontFamily: "Arial", fontSize: 15, lineHeight: 1.6,
                              color: "#1A1A30", marginBottom: 12 }}
         dangerouslySetInnerHTML={{ __html: buf.map(inline).join("<br/>") }} />
    );
  }
  return out;
}

export default function LegalPage({ title, markdown }) {
  useEffect(() => { document.title = title; }, [title]);
  return (
    <div className="admin-force-light" style={{ minHeight: "100dvh", background: "#FDFBF7" }}>
      <header style={{ maxWidth: 680, margin: "0 auto", padding: "24px 20px 0" }}>
        <Link to="/" data-testid="legal-home-link"
              style={{ fontFamily: "Poppins", fontWeight: 800, fontSize: 18,
                       textTransform: "lowercase", color: "#1A1A30", textDecoration: "none" }}>
          watu wako
        </Link>
      </header>
      <main style={{ maxWidth: 680, margin: "0 auto", padding: "16px 20px 64px" }}
            data-testid="legal-page">
        {renderBlocks(markdown)}
        <p style={{ marginTop: 36, fontFamily: "Arial", fontSize: 13, color: META }}>
          <Link to="/privacy" style={{ color: "#1A1A30", textDecoration: "underline", marginRight: 14 }}>privacy policy</Link>
          <Link to="/terms" style={{ color: "#1A1A30", textDecoration: "underline" }}>terms of use</Link>
        </p>
      </main>
    </div>
  );
}
